import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useAsyncFn } from 'react-use';

import { logger } from '@service-initiation/shared-clients-osp-logger';
import { PaymentIntentStatus } from '@service-initiation/shared-clients-tech-care-services';
import { useAsyncAtom } from '@service-initiation/shared-utils-jotai';
import {
  billingDetailsAtom,
  bookingWindowIdAtom,
  customerDetailsAtom,
  deviceDetailsAsyncAtom,
  remoteTechAtom,
  selectedAppointmentAsyncAtom,
  selectedRepairsAsyncAtom,
  store,
  useBillingDetailsAtom,
} from '@service-initiation/tech-care-data-access-store';
import { createPaymentIntent } from '@service-initiation/tech-care-data-access-tech-care-services';

export const useCreatePaymentIntent = () => {
  const { value: appointment } = useAsyncAtom(selectedAppointmentAsyncAtom);
  const appointmentIntentId = appointment?.intentId;
  const [, setBillingDetails] = useBillingDetailsAtom();
  const stripe = useStripe();
  const stripeElements = useElements();

  return useAsyncFn(async () => {
    const ClientCardElement = stripeElements?.getElement(CardNumberElement);
    const selectedRepairs = await store.get(selectedRepairsAsyncAtom);
    const deviceDetails = await store.get(deviceDetailsAsyncAtom);
    const bookingWindowId = store.get(bookingWindowIdAtom);
    const remoteTech = store.get(remoteTechAtom);
    const billingDetails = store.get(billingDetailsAtom);
    const customerDetails = store.get(customerDetailsAtom);

    try {
      if (stripe && stripeElements && ClientCardElement) {
        const response = await createPaymentIntent({
          addressLineOne:
            (billingDetails?.billingIsSame
              ? remoteTech?.meetingAddress?.streetAddress
              : billingDetails?.billingAddress?.line1) || '',
          addressLineTwo: billingDetails?.billingIsSame
            ? remoteTech?.meetingAddress?.address2
            : billingDetails?.billingAddress?.line2,
          bookingWindowId: bookingWindowId || '',
          city:
            (billingDetails?.billingIsSame
              ? remoteTech?.meetingAddress?.city
              : billingDetails?.billingAddress?.city) || '',
          device_type_option_ids:
            deviceDetails?.selectedDeviceColorOption?.option_ids || [],
          deviceRepairId: selectedRepairs?.[0].id || 0,
          deviceTypeId: deviceDetails?.id || 0,
          email: customerDetails?.email || '',
          name: `${billingDetails?.firstName} ${billingDetails?.lastName}`,
          phone: customerDetails?.phoneNumber || '',
          postalCode:
            (billingDetails?.billingIsSame
              ? remoteTech?.meetingAddress?.zip
              : billingDetails?.billingAddress?.postalCode) || '',
          state:
            (billingDetails?.billingIsSame
              ? remoteTech?.meetingAddress?.state
              : billingDetails?.billingAddress?.state) || '',
        });

        if (response.status === PaymentIntentStatus.RequiresPaymentMethod) {
          const stripeResult = await stripe.confirmCardPayment(
            response.client_secret,
            {
              payment_method: {
                billing_details: {
                  address: {
                    city: billingDetails?.billingAddress?.city,
                    country: 'US',
                    line1: billingDetails?.billingAddress?.line1,
                    line2: billingDetails?.billingAddress?.line2,
                    postal_code: billingDetails?.billingAddress?.postalCode,
                    state: billingDetails?.billingAddress?.state,
                  },
                  name: `${billingDetails?.firstName} ${billingDetails?.lastName}`,
                },
                card: ClientCardElement,
              },
            }
          );

          if (stripeResult?.error) {
            const error = {
              ...stripeResult.error,
              message: stripeResult.error.message,
              name: 'billing details error',
            } as Error;
            logger.error(error, {
              data: {
                damageType: selectedRepairs?.[0].id,
                deviceDetails: deviceDetails?.id,
                intentId: appointmentIntentId,
              },
            });
            throw error;
          } else if (stripeResult.paymentIntent.status === 'succeeded') {
            setBillingDetails({
              ...billingDetails,
              payment_intent_id: response.payment_intent_id,
            });
          }
        }
      }
    } catch (error) {
      const err = new Error('Error occurred while creating payment');
      logger.error(err, {
        data: {
          damageType: selectedRepairs?.[0].id,
          deviceDetails: deviceDetails?.id,
          intentId: appointmentIntentId,
        },
      });
      throw err;
    }
  }, [stripe, stripeElements, setBillingDetails]);
};
